import { createColumnHelper } from '@tanstack/react-table';
import { LocalizedText } from '@mews/framework';
import { ConvertedCurrencyValue, Dash, LocalizedDateTimeFromUtc, TooltippedTag } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { PreauthorizationTableRecord } from '@mews-commander/data-access';
import { WithAccountId } from '@mews-commander/types';
import { getTagVariantFromPreauthorizationState, preauthorizationStatusTextKeyMap } from './utils';
import { CreatedCell } from './CreatedCell';
import { PaymentCardCell } from './PaymentCardCell';
import { PreauthorizationsActionsMenu } from './PreauthorizationsActionsMenu';

const columnHelper = createColumnHelper<PreauthorizationTableRecord>();

export const getPreauthorizationsColumns = (props: { refetchTableData: () => void } & WithAccountId) => [
    columnHelper.accessor('card', {
        header: () => <LocalizedText textKey={TextKey.PaymentCard} />,
        cell: ({
            row: {
                original: { card },
            },
        }) => {
            if (!card) {
                return <Dash />;
            }

            const { format, lastFourDigits, type, paymentTerminal } = card;
            return (
                <PaymentCardCell
                    paymentData={{
                        lastFourCardDigits: lastFourDigits,
                        paymentCardFormat: format,
                        paymentCardType: type,
                        paymentTerminal,
                    }}
                />
            );
        },
    }),
    columnHelper.accessor('sequenceCode', {
        header: () => <LocalizedText textKey={TextKey.SequenceCode} />,
    }),
    columnHelper.accessor('code', {
        header: () => <LocalizedText textKey={TextKey.Code} />,
    }),
    columnHelper.accessor('identifier', {
        header: () => <LocalizedText textKey={TextKey.Identifier} />,
    }),
    columnHelper.accessor('expirationUtc', {
        header: () => <LocalizedText textKey={TextKey.Expiration} />,
        cell: ({
            row: {
                original: { expirationUtc },
            },
        }) => expirationUtc && <LocalizedDateTimeFromUtc utcString={expirationUtc} />,
    }),
    columnHelper.accessor('createdUtc', {
        header: () => <LocalizedText textKey={TextKey.Created} />,
        cell: ({
            row: {
                original: { createdUtc, creatorProfileName },
            },
        }) => <CreatedCell createdUtc={createdUtc} creatorProfileName={creatorProfileName} />,
    }),
    columnHelper.accessor('notes', {
        header: () => <LocalizedText textKey={TextKey.Notes} />,
    }),
    columnHelper.accessor('status', {
        header: () => <LocalizedText textKey={TextKey.Status} />,
        cell: ({
            row: {
                original: { status, transactionDetails },
            },
        }) =>
            status && (
                <TooltippedTag
                    variant={getTagVariantFromPreauthorizationState[status]}
                    tooltip={transactionDetails}
                    outline
                >
                    <LocalizedText textKey={preauthorizationStatusTextKeyMap[status]} />
                </TooltippedTag>
            ),
    }),
    columnHelper.accessor('amount', {
        header: () => <LocalizedText textKey={TextKey.Value} />,
        cell: ({
            row: {
                original: { amount },
            },
        }) => <ConvertedCurrencyValue amount={amount} />,
    }),
    columnHelper.accessor('actions', {
        header: () => <LocalizedText textKey={TextKey.ActionPlural} />,
        meta: {
            align: 'end',
        },
        cell: ({
            row: {
                original: { amount, card, id, actions },
            },
        }) =>
            actions && (
                <PreauthorizationsActionsMenu
                    actions={actions}
                    preauthorizationId={id}
                    amount={amount}
                    card={card}
                    {...props}
                />
            ),
    }),
];
